// @flow
import React, { Component, type Node } from 'react';
import { MerchantsDiv } from '../../MerchantsStyles';
import {
  CustomShowModelListDiv,
  DetailedShowInfoDiv,
  InfoItemDiv,
  PromoSpan
} from './DirectMerchantsShowStyles';
import { DefaultIcon } from '../../../../components/Icon';
import { formatCurrency } from '../../../../utilities/numberFormatting';
import {
  type Merchant,
  type Currencies,
  type TooltipProps,
  type Currency
} from '../../../../utilities/types';
import { dig } from '../../../../utilities/misc';
import moment from 'moment';
import {
  ModelListTable,
  ModelListTh
} from '../../../../components/ModelListStyles';
import { Default, Mobile } from '../../../../components/Responsive';
import TableLoader from '../../../../components/TableLoader';
import EmptyTable from '../../../../components/EmptyTable';
import DirectMerchantRow from './DirectMerchantRow';
import SectionLoader from '../../../../components/SectionLoader';
import {
  XIconWrapDiv,
  XIconWrapLink
} from '../../../../modules/CrudTable/CrudTableStyles';
import DefaultModelRowTr from '../../../../modules/CrudTable/components/DefaultModelRowTr';

type Props = {
  loading: boolean,
  directMerchant: Merchant,
  currencies: Currencies,
  showMostRecentOnly?: boolean,
  handleClick: () => void,
  ...$Exact<TooltipProps>,
  totalProcessingVolUsd: string,
  totalFeesUsd: string,
  partnerCurrency: Currency
};

export default class DirectMerchantShowPanel extends Component<Props> {
  renderBody = () => {
    if (this.props.loading) {
      return <TableLoader columns={6} />;
    } else if (this.props.directMerchant.processing_sums.length === 0) {
      return (
        <EmptyTable
          text="No merchant transactions to show."
          columns={6}
          model="Direct Merchant"
          img="receipt"
        />
      );
    } else {
      return this.props.directMerchant.processing_sums.map<Node>(
        (processing_sum, index) => (
          <React.Fragment key={index}>
            <DirectMerchantRow
              key={index}
              index={index}
              currencies={this.props.currencies}
              processing_sum={processing_sum}
              totalProcessingVolUsd={this.props.totalProcessingVolUsd}
              totalFeesUsd={this.props.totalFeesUsd}
              partnerCurrency={this.props.partnerCurrency}
              activeTooltip={this.props.activeTooltip}
              showTooltip={this.props.showTooltip}
              tableTotal={
                (this.props.directMerchant.processing_sums || []).length
              }
            ></DirectMerchantRow>
          </React.Fragment>
        )
      );
    }
  };

  render() {
    const currency =
      this.props.directMerchant &&
      this.props.currencies[this.props.directMerchant.currency];
    const hasFeeFixedPromo = dig(
      this.props.directMerchant,
      'promo',
      'fee_fixed'
    );
    const hasFeePercentPromo = dig(
      this.props.directMerchant,
      'promo',
      'fee_percent'
    );
    const promoFeePercent = dig(
      this.props.directMerchant,
      'promo',
      'fee_percent'
    );
    const promoFeeFixed = dig(this.props.directMerchant, 'promo', 'fee_fixed');
    return (
      <MerchantsDiv showMostRecentOnly={this.props.showMostRecentOnly}>
        <CustomShowModelListDiv hideBorderTop={this.props.showMostRecentOnly}>
          {this.props.showMostRecentOnly ? (
            <XIconWrapDiv>
              <DefaultIcon
                colorName="dustyGray"
                name="x"
                onClick={this.props.handleClick}
              />
            </XIconWrapDiv>
          ) : (
            <XIconWrapLink to={'/merchants'}>
              <DefaultIcon colorName="dustyGray" name="x" />
            </XIconWrapLink>
          )}
          {this.props.loading ? (
            <SectionLoader />
          ) : (
            <>
              <h5>{this.props.directMerchant.business_name}</h5>

              <DetailedShowInfoDiv>
                <InfoItemDiv>
                  <h5>ID</h5>
                  <p>{this.props.directMerchant.id}</p>
                </InfoItemDiv>
                {this.props.directMerchant.first_name && (
                  <InfoItemDiv>
                    <h5>Name</h5>
                    <p>
                      {this.props.directMerchant.first_name}{' '}
                      {this.props.directMerchant.last_name}
                    </p>
                  </InfoItemDiv>
                )}
                {this.props.directMerchant.email && (
                  <InfoItemDiv>
                    <h5>Email</h5>
                    <p>{this.props.directMerchant.email}</p>
                  </InfoItemDiv>
                )}
                <InfoItemDiv>
                  <h5>Trans. Fees</h5>
                  <p>
                    {hasFeeFixedPromo ? (
                      <>
                        <PromoSpan>
                          {formatCurrency(
                            this.props.directMerchant.transaction_fee,
                            currency
                          )}
                        </PromoSpan>
                        {formatCurrency(promoFeeFixed || NaN, currency)}- Promo
                        Ends {this.props.directMerchant.promo.end_date}
                      </>
                    ) : (
                      formatCurrency(
                        this.props.directMerchant.transaction_fee,
                        currency
                      )
                    )}
                  </p>
                </InfoItemDiv>
                <InfoItemDiv>
                  <h5>Trans. Percent</h5>
                  <p>
                    {hasFeePercentPromo ? (
                      <>
                        <PromoSpan>
                          {this.props.directMerchant.transaction_fee_percent} %
                        </PromoSpan>
                        {formatCurrency(promoFeePercent || NaN, currency)} % -
                        Promo Ends {this.props.directMerchant.promo.end_date}
                      </>
                    ) : (
                      <>
                        {this.props.directMerchant.transaction_fee_percent} %{' '}
                      </>
                    )}
                  </p>
                </InfoItemDiv>
                <InfoItemDiv>
                  <h5>Account Created</h5>
                  <p>
                    {' '}
                    {moment(this.props.directMerchant.created_at).format(
                      'MM/DD/YY'
                    )}
                  </p>
                </InfoItemDiv>
                {this.props.directMerchant.application_status ===
                  'approved' && (
                  <InfoItemDiv>
                    <h5>Account Approved</h5>
                    <p>
                      {moment(this.props.directMerchant.approved_at).format(
                        'MM/DD/YY'
                      )}
                    </p>
                  </InfoItemDiv>
                )}
              </DetailedShowInfoDiv>
              <ModelListTable>
                <thead>
                  <DefaultModelRowTr>
                    <Default>
                      <ModelListTh>Currency</ModelListTh>
                      <ModelListTh right>Processing Vol.</ModelListTh>
                    </Default>
                    <Mobile>
                      <ModelListTh>Processing Vol.</ModelListTh>
                    </Mobile>
                    <Default>
                      <ModelListTh right> Trans. Fees </ModelListTh>
                    </Default>
                    <ModelListTh right> Comm. Earned</ModelListTh>
                  </DefaultModelRowTr>
                </thead>
                <tbody>{this.renderBody()}</tbody>
              </ModelListTable>
            </>
          )}
        </CustomShowModelListDiv>
      </MerchantsDiv>
    );
  }
}
